import { Component, effect, inject } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { ForecastSeriesCompletedWithoutMeasure } from '@api-response-types/forecast-series.response';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartColorService } from 'demand-forecasting/services/chart/chart-color.service';
import { FeatureTranslationService } from 'demand-forecasting/services/feature-translation.service';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { latestCompletedForecastSeries } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { FeatureImportance } from 'demand-forecasting/types/feature-importance.interface';
import ExplanationChartOptions from 'demand-forecasting/types/forecast-chart/explanation-chart-options';
import {
  BaseChartOptions,
  HighchartsOptions,
} from 'demand-forecasting/types/forecast-chart/highcharts-options';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { Subscription } from 'rxjs';

type SeriesObject = {
  name: string;
  data: Array<number | null>;
  // color: string;
  // type: string;
};
@Component({
  selector: 'pm-forecast-feature-importance',
  templateUrl: './forecast-feature-importance.component.html',
  styleUrls: ['./forecast-feature-importance.component.scss'],
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatCard,
    MatCardContent,
    TranslateModule,
    HighchartsChartComponent,
  ],
})
export class ForecastFeatureImportanceComponent {
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly featureTranslationService = inject(
    FeatureTranslationService,
  );
  private readonly colorService = inject(ChartColorService);
  forecastSeries = this.store.selectSignal(latestCompletedForecastSeries);
  private readonly configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );

  featureImportances: FeatureImportance[] = [];
  subscriptions: Subscription[] = [];
  chartOptions: HighchartsOptions = BaseChartOptions.getEmptyChartOptions();
  chart?: Highcharts.Chart;
  chartUpdated = false;
  allPlainCategories: string[] = [];
  maxBarsTpDisplay = 10;

  constructor() {
    // TODO could be improved by only extract new features and not all every time
    this.chartOptions = ExplanationChartOptions.getChartOptions();
    effect(() => {
      const forecastSeries = this.forecastSeries();
      const configuration = this.configuration();
      if (!forecastSeries || !configuration) {
        // this effect is triggered when navigating back to configurations page -> just return
        return;
      }
      this.createChartSeriesAndUpdate(forecastSeries);
      this.setValueRangeOfBars();
      this.chartOptions = ExplanationChartOptions.patchAmountOfBarsToDisplay(
        this.chartOptions,
        this.maxBarsTpDisplay,
      );
    });
  }

  setXAxisTitle(): void {
    const axisTitle = {
      text: this.translateService.instant('RESULT.IMPORTANCE.X_AXIS'),
    };
    this.chartOptions.xAxis.title = axisTitle;
  }

  setYAxisTitle(): void {
    const axisTitle = {
      text: this.translateService.instant('RESULT.IMPORTANCE.Y_AXIS'),
    };
    this.chartOptions.yAxis.title = axisTitle;
  }

  createChartSeriesAndUpdate(
    forecastSeries: ForecastSeriesCompletedWithoutMeasure,
  ): void {
    this.chartOptions.series = [];

    this.setChartCategories();
    this.featureImportances = [];
    if (!forecastSeries) {
      console.error('Cannot setup chart categories. No forecast series found.');
    } else {
      this.extractFeatureImportances(forecastSeries);
    }
    this.featureImportances.forEach((featureImportance) => {
      const seriesObject: SeriesObject = {
        name: featureImportance.filterName,
        data: [],
        // color: featureImportance.color,
        // type: 'bar',
      };
      this.allPlainCategories.forEach((categoryFeature) => {
        const feature = featureImportance.features.find(
          (ftr) => ftr.key === categoryFeature,
        );
        if (!feature) throw new Error('feature not found.');
        // TODO improve this so that NaN is not possible
        const val = feature.value * 100;
        const processedValue =
          Number.isNaN(val) || val === undefined
            ? null
            : Math.round((val + Number.EPSILON) * 100) / 100;

        seriesObject.data.push(processedValue);
      });

      this.chartOptions.series.push({
        name: seriesObject.name,
        data: seriesObject.data,
        type: 'bar',
      });
    });
    if (this.chart) {
      Promise.resolve().then(() => {
        this.chartUpdated = true;
      });
    }
    this.setXAxisTitle();
    this.setYAxisTitle();
    this.chartOptions.legend = { enabled: false };
  }

  setChartCategories(): void {
    this.chartOptions.xAxis.categories = [];
    this.allPlainCategories = [];

    const forecastItem = this.forecastSeries();
    if (!forecastItem?.featureImportance) {
      return;
    }

    this.allPlainCategories = Object.entries(forecastItem.featureImportance)
      .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
      .filter((el) => el[1] >= 0.0001)
      .map((el) => el[0])
      .filter((key) => key !== '_baseline');

    this.chartOptions.xAxis.categories =
      this.featureTranslationService.translateFeatures(this.allPlainCategories);
  }

  extractFeatureImportances(
    forecastSeriesItem: ForecastSeriesCompletedWithoutMeasure,
  ): void {
    if (!forecastSeriesItem) {
      return;
    }

    const featureImportance: FeatureImportance = {
      filterName: this.translateService.instant(forecastSeriesItem.name),
      color:
        forecastSeriesItem.colors[0] ??
        this.colorService.getColors('aggregated')[0],
      features: [],
    };
    Object.keys(forecastSeriesItem.featureImportance).forEach((key) =>
      featureImportance.features.push({
        key,
        value: forecastSeriesItem.featureImportance[key],
      }),
    );
    this.featureImportances.push(featureImportance);
  }

  setChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  /**
   * Sets the min and max value of the y axis to the min and max value of the data. This is necessary to avoid automatically zooming when scrolling the chart
   */
  private setValueRangeOfBars(): void {
    const values = this.chartOptions.series.flatMap(
      (series) =>
        ((series as Highcharts.SeriesBarOptions).data as Array<
          number | null
        >) ?? [],
    );
    const numericValues = values.filter(
      (v): v is number => typeof v === 'number' && !Number.isNaN(v),
    );

    this.chartOptions.yAxis.min = 0;
    this.chartOptions.yAxis.max =
      numericValues.length > 0 ? Math.max(...numericValues) : null;
  }
}
